import { TransportUnitNode, UnitTypeOption } from '../types';
import { getTransportUnitPositions } from './transportUnitPositions';

/**
 * Which unit types each canonical `UnitType` may hold directly, most preferred first: a new
 * sub-unit starts as the first entry the spec offers. A process spec decides which unit types a
 * receiver may choose but cannot express which nestings are valid, so that rule lives here. A type
 * missing from this table (a value a spec adds later) is unknown to `canNest`.
 */
const ALLOWED_CHILD_UNIT_TYPES: Readonly<Record<string, ReadonlyArray<string>>> = {
  CONTAINER: ['PALLET', 'EURO_PALLET', 'EURO_PALLET_HALF', 'EURO_PALLET_QUARTER', 'CARTON', 'PACKAGE'],
  PALLET: ['CARTON', 'PACKAGE'],
  EURO_PALLET: ['CARTON', 'PACKAGE', 'EURO_PALLET_HALF', 'EURO_PALLET_QUARTER'],
  EURO_PALLET_HALF: ['CARTON', 'PACKAGE'],
  EURO_PALLET_QUARTER: ['CARTON', 'PACKAGE'],
  CARTON: ['CARTON', 'PACKAGE'],
  PACKAGE: ['CARTON', 'PACKAGE'],
};

/**
 * Canonical display order for unit-type options, largest to smallest. A dropdown needs one fixed
 * order, which the allowed-children table does not give (the pallet family has no order there).
 */
const UNIT_TYPE_DISPLAY_ORDER: ReadonlyArray<string> = [
  'CONTAINER',
  'PALLET',
  'EURO_PALLET',
  'EURO_PALLET_HALF',
  'EURO_PALLET_QUARTER',
  'CARTON',
  'PACKAGE',
];

/**
 * Sorts unit-type options into `UNIT_TYPE_DISPLAY_ORDER`, so the choices read in that fixed order
 * instead of whatever order the process spec happened to declare them in. A type the hierarchy
 * doesn't know about (a future spec addition) always sorts after every known one,
 * alphabetically by label among themselves.
 */
export const sortUnitTypeOptions = (options: Array<UnitTypeOption>): Array<UnitTypeOption> =>
  [...options].sort((a, b) => {
    const orderA = UNIT_TYPE_DISPLAY_ORDER.indexOf(a.value);
    const orderB = UNIT_TYPE_DISPLAY_ORDER.indexOf(b.value);
    const rankA = orderA === -1 ? Infinity : orderA;
    const rankB = orderB === -1 ? Infinity : orderB;
    return rankA !== rankB ? rankA - rankB : a.label.localeCompare(b.label);
  });

export type InvalidNesting = {
  childPosition: string;
  childUnitType: string;
  parentPosition: string;
  parentUnitType: string;
};

/**
 * Every sub-unit whose type its parent cannot hold, per `ALLOWED_CHILD_UNIT_TYPES`.
 *
 * `getAllowedUnitTypeOptions` stops these being chosen, so one can only appear in an older
 * document; it is flagged on Apply, not passed through silently.
 */
export const findInvalidNestings = (transportUnits: Array<TransportUnitNode>): Array<InvalidNesting> => {
  const positions = getTransportUnitPositions(transportUnits);
  const found: Array<InvalidNesting> = [];

  const walk = (nodes: Array<TransportUnitNode>, parent: TransportUnitNode | undefined): void => {
    for (const node of nodes) {
      if (parent && !canNest(parent.unitType, node.unitType)) {
        found.push({
          childPosition: positions.get(node.id) ?? '',
          childUnitType: node.unitType,
          parentPosition: positions.get(parent.id) ?? '',
          parentUnitType: parent.unitType,
        });
      }
      walk(node.children, node);
    }
  };

  walk(transportUnits, undefined);
  return found;
};

/**
 * Unit-type choices for a node: the types its parent may hold. The node's own children do not
 * restrict it, so a user can always retype a parent; a child the new type cannot hold is flagged
 * (`canNest` on the card, `findInvalidNestings` on Apply), not prevented.
 *
 * `keep`, when given, is always included even if it fails the check, so a unit from an older
 * document with an invalid nesting keeps showing its real value instead of the dropdown going blank.
 */
export const getAllowedUnitTypeOptions = (
  options: Array<UnitTypeOption>,
  { parentUnitType, keep }: { parentUnitType?: string; keep?: string },
): Array<UnitTypeOption> =>
  options.filter((option) => option.value === keep || !parentUnitType || canNest(parentUnitType, option.value));

/**
 * The default type for a brand-new unit given its (optional) parent: the parent's most preferred
 * child that the spec offers. Without a known preference (a root unit, an unknown parent, or only
 * unknown options) it is the first allowed option, so `options` must already be sorted by
 * `sortUnitTypeOptions`.
 */
export const getDefaultChildUnitType = (
  options: Array<UnitTypeOption>,
  parentUnitType: string | undefined,
): string | undefined => {
  const offered = new Set(options.map((option) => option.value));
  const preferred = parentUnitType
    ? ALLOWED_CHILD_UNIT_TYPES[parentUnitType]?.find((type) => offered.has(type))
    : undefined;
  return preferred ?? getAllowedUnitTypeOptions(options, { parentUnitType })[0]?.value;
};

/**
 * Whether a unit of `childUnitType` may be packed directly inside a unit of `parentUnitType`.
 * An unknown parent restricts nothing and an unknown child is always allowed, so a type a spec
 * adds later degrades quietly instead of warning or vanishing from a dropdown.
 */
export const canNest = (parentUnitType: string, childUnitType: string): boolean => {
  const allowedChildren = ALLOWED_CHILD_UNIT_TYPES[parentUnitType];
  if (allowedChildren === undefined || !(childUnitType in ALLOWED_CHILD_UNIT_TYPES)) {
    return true;
  }
  return allowedChildren.includes(childUnitType);
};
